import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquarePlus, Sparkles, Upload, X, Loader2 } from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { useI18n } from '../i18n';
import ChatMessage, { StreamingMessage } from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import ModelSelector from '../components/ModelSelector';
import ImageMetadataViewer from '../components/ImageMetadataViewer';
import { mediaApi } from '../lib/api';

export default function Chat() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [externalFiles, setExternalFiles] = useState<File[]>([]);
    const [selectedImagePath, setSelectedImagePath] = useState<string | null>(null);
    const [selectedMetadata, setSelectedMetadata] = useState<Record<string, unknown> | null>(null);
    const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
    const { t } = useI18n();

    const {
        messages,
        isLoadingMessages,
        isStreaming,
        streamingContent,
        currentChatId,
        loadChat,
        setCurrentChat,
    } = useChatStore();

    // Load chat when ID changes
    useEffect(() => {
        if (id) {
            const chatId = parseInt(id);
            if (chatId !== currentChatId) {
                loadChat(chatId, () => {
                    // 404 - redirect to home
                    navigate('/', { replace: true });
                });
            }
        } else {
            setCurrentChat(null);
        }
    }, [id, currentChatId, loadChat, setCurrentChat, navigate]);

    // Scroll to bottom on new messages
    useEffect(() => {
        // Small delay to ensure DOM has rendered
        const timer = setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
        return () => clearTimeout(timer);
    }, [messages, streamingContent]);

    // Drag and drop handlers for entire chat area
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set false if leaving the main container
        if (e.currentTarget === e.target) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files).filter(
            file => file.type.startsWith('image/') || file.type.startsWith('video/')
        );
        if (files.length > 0) {
            setExternalFiles(prev => [...prev, ...files].slice(0, 5));
        }
    }, []);

    // Clear external files after they're consumed
    const handleFilesConsumed = useCallback(() => {
        setExternalFiles([]);
    }, []);

    // Handle image click to open modal
    const handleImageClick = async (imagePath: string) => {
        setSelectedImagePath(imagePath);
        setSelectedMetadata(null);
        setIsLoadingMetadata(true);

        try {
            // Try to load .json metadata file from R2
            const metadataPath = imagePath.replace(/\.(png|webp|jpg|jpeg|gif)$/i, '.json');
            const metadataUrl = mediaApi.getFileUrl(metadataPath);
            const response = await fetch(metadataUrl, { credentials: 'include' });

            if (response.ok) {
                const metadata = await response.json();
                setSelectedMetadata(metadata);
            }
        } catch (error) {
            console.log('No metadata file found for:', imagePath);
        } finally {
            setIsLoadingMetadata(false);
        }
    };

    // Empty state (no chat selected)
    if (!id && !currentChatId) {
        return (
            <div
                className="flex flex-col h-full relative"
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {/* Drag overlay */}
                {isDragging && (
                    <div className="absolute inset-0 z-50 bg-dark-900/90 flex items-center justify-center border-2 border-dashed border-accent-500 rounded-lg">
                        <div className="text-center">
                            <Upload className="w-12 h-12 text-accent-400 mx-auto mb-4" />
                            <p className="text-lg text-dark-200">{t.gallery.upload}</p>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between px-4 py-3 border-b border-dark-800">
                    <ModelSelector />
                </div>

                {/* Empty state */}
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-6">
                            <Sparkles className="w-10 h-10 text-accent-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-dark-100 mb-3">
                            {t.chat.startNewChat}
                        </h2>
                        <p className="text-dark-400 mb-6">
                            {t.chat.noChats}
                        </p>
                        <div className="flex items-center justify-center gap-2 text-sm text-dark-500">
                            <MessageSquarePlus className="w-4 h-4" />
                            {t.chat.newChat}
                        </div>
                    </div>
                </div>

                {/* Input */}
                <ChatInput
                    externalFiles={externalFiles}
                    onExternalFilesConsumed={handleFilesConsumed}
                />
            </div>
        );
    }

    return (
        <div
            className="flex flex-col h-full relative"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Drag overlay */}
            {isDragging && (
                <div className="absolute inset-0 z-50 bg-dark-900/90 flex items-center justify-center border-2 border-dashed border-accent-500 rounded-lg">
                    <div className="text-center">
                        <Upload className="w-12 h-12 text-accent-400 mx-auto mb-4" />
                        <p className="text-lg text-dark-200">{t.gallery.upload}</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-dark-800">
                <ModelSelector />
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto">
                {isLoadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="spinner text-accent-500 w-8 h-8" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full p-8">
                        <div className="text-center">
                            <Sparkles className="w-12 h-12 text-accent-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-dark-200 mb-2">
                                {t.chat.startNewChat}
                            </h3>
                            <p className="text-dark-400">
                                {t.chat.typeMessage}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-dark-800/50">
                        {messages.map((message) => (
                            <ChatMessage
                                key={message.id}
                                message={message}
                                onImageClick={handleImageClick}
                            />
                        ))}

                        {/* Streaming response */}
                        {isStreaming && streamingContent && (
                            <StreamingMessage content={streamingContent} />
                        )}

                        {/* Typing indicator when streaming but no content yet */}
                        {isStreaming && !streamingContent && (
                            <div className="flex items-center gap-3 p-4">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center">
                                    <div className="typing-indicator text-white scale-75">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                                <span className="text-sm text-dark-400">{t.chat.thinking}</span>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Input */}
            <ChatInput
                externalFiles={externalFiles}
                onExternalFilesConsumed={handleFilesConsumed}
            />

            {/* Image Modal */}
            {selectedImagePath && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-start justify-center overflow-auto p-4">
                    <div className="relative w-full max-w-6xl bg-dark-900 rounded-xl my-4">
                        {/* Close button */}
                        <button
                            onClick={() => setSelectedImagePath(null)}
                            className="absolute top-4 right-4 p-2 bg-dark-800 rounded-lg z-10 hover:bg-dark-700"
                        >
                            <X className="w-5 h-5 text-dark-300" />
                        </button>

                        <div className="flex flex-col md:flex-row">
                            {/* Image */}
                            <div className="flex-1 p-4 flex items-center justify-center bg-dark-950/50">
                                <img
                                    src={mediaApi.getFileUrl(selectedImagePath)}
                                    alt="Selected image"
                                    className="max-w-full max-h-[80vh] object-contain"
                                />
                            </div>

                            {/* Metadata */}
                            <div className="w-full md:w-80 p-4 border-t md:border-t-0 md:border-l border-dark-800 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
                                {isLoadingMetadata ? (
                                    <div className="text-center py-4">
                                        <Loader2 className="w-6 h-6 text-accent-400 mx-auto animate-spin" />
                                        <p className="text-sm text-dark-400 mt-2">메타데이터 로드 중...</p>
                                    </div>
                                ) : (
                                    <ImageMetadataViewer
                                        metadata={selectedMetadata as any}
                                        filename={selectedImagePath.split('/').pop() || ''}
                                        path={selectedImagePath}
                                        imageUrl={mediaApi.getFileUrl(selectedImagePath)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

